/* eslint-disable arrow-body-style */
/* eslint-disable no-unused-expressions */
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  TablePagination,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  FormControl,
  Divider,
  Switch,
  Box,
} from '@mui/material';
import { Container } from 'reactstrap';
import { useTheme } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import { Form, Formik } from 'formik';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import axios from '../../utils/axios';
import UIContext from '../../context/UIContext';

const PincodeInitialValue = {
  PincodeId: null,
  Pincode: '',
  City: '',
  State: '',
  IsServiceable: 'Y',
  IsFreeDelivery: 'N',
  FreeDeliveryMinCart: 0,
  ShippingCharges: 0, // Added shipping charges field
  Active: 'Y',
};

const filterInitialValue = {
  searchTerm: '',
  Active: true,
};

const PinCode = () => {
  const [filterData, setFilterData] = useState(filterInitialValue);
  const [allData, setAllData] = useState([]);
  const [initialValue, setInitialValue] = useState(PincodeInitialValue);
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);

  const { openSnackBar, showLoading, hideLoading } = useContext(UIContext);

  useEffect(() => {
    GetAllData();
  }, []);

  const validationSchema = Yup.object().shape({
    Pincode: Yup.string()
      .matches(/^[0-9]{6}$/, 'Enter 6-digit pincode')
      .required('Required'),
    City: Yup.string().required('City is required'),
    State: Yup.string().required('State is required'),
    IsServiceable: Yup.string().oneOf(['Y', 'N']).required(),
    IsFreeDelivery: Yup.string().oneOf(['Y', 'N']).required(),
    FreeDeliveryMinCart: Yup.number().min(0).required('Required'),
    ShippingCharges: Yup.number().min(0).required('Shipping charges are required'), // Added validation
    Active: Yup.string().oneOf(['Y', 'N']).required(),
  });

  const GetAllData = async () => {
    try {
      showLoading();
      const res = await axios.get('Master/PinCode/GetAllPinCode');
      setAllData(res.data);
    } catch (e) {
      openSnackBar('Failed to fetch pincodes', 'error');
    }
    hideLoading();
  };

  const InsertData = async (values) => {
    try {
      showLoading();
      await axios.post('Master/PinCode/InsertPinCode', values);
      handleSubmitSuccess();
    } catch (e) {
      openSnackBar('Insert failed', 'error');
    }
    hideLoading();
  };

  const UpdateData = async (values) => {
    try {
      showLoading();
      await axios.post('Master/PinCode/UpdatePinCode', values);
      handleSubmitSuccess();
    } catch (e) {
      openSnackBar('Update failed', 'error');
    }
    hideLoading();
  };

  const DeleteData = async () => {
    try {
      showLoading();
      await axios.delete(`Master/PinCode/DeletePinCode/${deleteId}`);
      openSnackBar('Deleted successfully!', 'success');
      GetAllData();
    } catch (e) {
      openSnackBar('Delete failed', 'error');
    }
    setOpenDelete(false);
    setDeleteId(null);
    hideLoading();
  };

  const handleSubmitSuccess = () => {
    GetAllData();
    setOpen(false);
    setEdit(false);
    setInitialValue(PincodeInitialValue);
    openSnackBar(edit ? 'Updated Successfully!' : 'Added Successfully!', edit ? 'warning' : 'success');
  };

  const handleEditClick = (item) => {
    setEdit(true);
    setInitialValue(item);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEdit(false);
    setInitialValue(PincodeInitialValue);
  };

  const filteredData = useMemo(() => {
    return allData.filter(
      (x) =>
        `${x.Pincode} ${x.City} ${x.State}`.toLowerCase().includes(filterData.searchTerm.toLowerCase()) &&
        x.Active === (filterData.Active ? 'Y' : 'N')
    );
  }, [allData, filterData]);

  return (
    <Container maxWidth="xl" sx={{ pt: 2 }}>
      <Paper elevation={3} sx={{ p: 1, mb: 2, backgroundColor: '#2A4759', color: 'white' }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h6">Pincode Master</Typography>
          </Grid>
          <Grid item>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setOpen(true)}
                startIcon={<PlaylistAddIcon />}
              >
                Add Pincode
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Filter */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={filterData.searchTerm}
              onChange={(e) => setFilterData({ ...filterData, searchTerm: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6} display="flex" alignItems="center" justifyContent="flex-end">
            <Typography mr={1}>Show Active:</Typography>
            <Switch
              checked={filterData.Active}
              onChange={(e) => setFilterData({ ...filterData, Active: e.target.checked })}
              color="primary"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Table View */}
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Pincode</TableCell>
              <TableCell>City</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Serviceable</TableCell>
              <TableCell>Free Delivery</TableCell>
              <TableCell>Min Cart</TableCell>
              <TableCell>Shipping Charges</TableCell> {/* Added column */}
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
              <TableRow key={row.PincodeId}>
                <TableCell>{row.Pincode}</TableCell>
                <TableCell>{row.City}</TableCell>
                <TableCell>{row.State}</TableCell>
                <TableCell>{row.IsServiceable === 'Y' ? 'Yes' : 'No'}</TableCell>
                <TableCell>{row.IsFreeDelivery === 'Y' ? 'Yes' : 'No'}</TableCell>
                <TableCell>{row.FreeDeliveryMinCart}</TableCell>
                <TableCell>{row.ShippingCharges}</TableCell> {/* Added cell */}
                <TableCell>
                  <Chip
                    label={row.Active === 'Y' ? 'Active' : 'Inactive'}
                    color={row.Active === 'Y' ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton color="primary" size="small" onClick={() => handleEditClick(row)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => {
                      setDeleteId(row.PincodeId);
                      setOpenDelete(true);
                    }}
                  >
                    <DeleteForeverIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Form Dialog */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">{edit ? 'Edit Pincode' : 'Add Pincode'}</Typography>
            <IconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Formik
            validationSchema={validationSchema}
            initialValues={initialValue}
            onSubmit={(values, { resetForm }) => {
              edit ? UpdateData(values) : InsertData(values);
              resetForm();
            }}
            enableReinitialize
          >
            {({ values, errors, touched, handleChange, handleBlur, submitForm }) => (
              <Form>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="Pincode"
                      name="Pincode"
                      fullWidth
                      size="small"
                      value={values.Pincode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.Pincode && !!errors.Pincode}
                      helperText={touched.Pincode && errors.Pincode}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="City"
                      name="City"
                      fullWidth
                      size="small"
                      value={values.City}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.City && !!errors.City}
                      helperText={touched.City && errors.City}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="State"
                      name="State"
                      fullWidth
                      size="small"
                      value={values.State}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.State && !!errors.State}
                      helperText={touched.State && errors.State}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Free Delivery Min Cart"
                      name="FreeDeliveryMinCart"
                      type="number"
                      fullWidth
                      size="small"
                      value={values.FreeDeliveryMinCart}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.FreeDeliveryMinCart && !!errors.FreeDeliveryMinCart}
                      helperText={touched.FreeDeliveryMinCart && errors.FreeDeliveryMinCart}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Shipping Charges"
                      name="ShippingCharges"
                      type="number"
                      fullWidth
                      size="small"
                      value={values.ShippingCharges}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.ShippingCharges && !!errors.ShippingCharges}
                      helperText={touched.ShippingCharges && errors.ShippingCharges}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <FormLabel>Is Serviceable</FormLabel>
                      <RadioGroup row name="IsServiceable" value={values.IsServiceable} onChange={handleChange}>
                        <FormControlLabel value="Y" control={<Radio />} label="Yes" />
                        <FormControlLabel value="N" control={<Radio />} label="No" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <FormLabel>Free Delivery</FormLabel>
                      <RadioGroup row name="IsFreeDelivery" value={values.IsFreeDelivery} onChange={handleChange}>
                        <FormControlLabel value="Y" control={<Radio />} label="Yes" />
                        <FormControlLabel value="N" control={<Radio />} label="No" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <FormLabel>Active</FormLabel>
                      <RadioGroup row name="Active" value={values.Active} onChange={handleChange}>
                        <FormControlLabel value="Y" control={<Radio />} label="Yes" />
                        <FormControlLabel value="N" control={<Radio />} label="No" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
                    <Button variant="outlined" onClick={handleCloseDialog}>
                      Cancel
                    </Button>
                    <Button variant="contained" color="primary" onClick={submitForm}>
                      {edit ? 'Update' : 'Submit'}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Paper>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle>Are you sure you want to delete this record?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={DeleteData}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PinCode;